/*
 *
 * AdminPage / Devices / DeviceMonitor
 *
 * Monitor of photo-booth device telemetry. Backed by
 * GET /api/v3/admin/device-statuses, which (unlike users/gifs) returns a BARE
 * ARRAY of DEVICE_STATUSES_DEFAULT_RESPONSE_SHAPE items — no pagination
 * envelope. The server paginates via page/per_page but emits no page metadata,
 * so we page with a next/prev "is this page full?" heuristic: a full page
 * (=== per_page rows) means more may follow.
 *
 * Uses a purpose-built telemetry table rather than the shared components/Table
 * — that one mandates its numbered paginator, which can't express the
 * metadata-less bare-array pagination here.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import Loader from "components/Loader";
import {
  useAdminDeviceStatuses,
  DEVICE_STATUSES_PER_PAGE,
} from "../api/queries";
import {
  Wrapper,
  Table,
  Th,
  Td,
  Row,
  Pager,
  PagerButton,
  StatusDot,
} from "./styledComponents";

import { isDeviceOnline, networkLabel } from "./network";

// Battery shown as a percentage; the API reports battery_level as a 0–1 float.
const fmtBattery = (level) =>
  level == null ? "—" : `${Math.round(Number(level) * 100)}%`;

const DeviceMonitor = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isFetching } = useAdminDeviceStatuses({ page });

  // The response is a bare array. A page that came back full means there may be
  // another; a short/empty page means we're at the end.
  const rows = Array.isArray(data) ? data : [];
  const hasNextPage = rows.length === DEVICE_STATUSES_PER_PAGE;
  const hasPrevPage = page > 1;

  if (isLoading) {
    return (
      <Wrapper>
        <h1>Device Status</h1>
        <div data-testid="devices-loading">
          <Loader />
        </div>
      </Wrapper>
    );
  }

  if (isError) {
    return (
      <Wrapper>
        <h1>Device Status</h1>
        <p className="admin-devices__error">Couldn’t load device statuses. Try again.</p>
      </Wrapper>
    );
  }

  if (rows.length === 0) {
    // On page 1 it's a true empty state. Past page 1 the user has paged beyond
    // the last device (the metadata-less API can't tell us the last page up
    // front), so we must keep a Previous control — otherwise they're stranded
    // at the boundary with no way back.
    return (
      <Wrapper>
        <h1>Device Status</h1>
        <p className="admin-devices__empty">
          {page > 1 ? "No more devices." : "No device statuses to show."}
        </p>
        {page > 1 && (
          <Pager>
            <PagerButton
              type="button"
              disabled={isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </PagerButton>
            <span className="admin-devices__page">Page {page}</span>
          </Pager>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <h1>Device Status</h1>
      <Table>
        <thead>
          <tr>
            <Th>Device</Th>
            <Th>iOS</Th>
            <Th>Battery</Th>
            <Th>Queue</Th>
            <Th>Network</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => {
            const open = () => navigate(`/admin/devices/${d.id}`);
            return (
            <Row
              key={d.id}
              role="button"
              tabIndex={0}
              aria-label={`View ${d.device_name || `device ${d.id}`}`}
              onClick={open}
              onKeyDown={(e) => {
                // Make the row navigation keyboard-operable (Enter / Space),
                // matching the pointer onClick — a bare clickable <tr> is not
                // reachable or actuatable by keyboard otherwise.
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  open();
                }
              }}
            >
              <Td>{d.device_name || "—"}</Td>
              <Td>{d.ios_version || "—"}</Td>
              <Td>{fmtBattery(d.battery_level)}</Td>
              <Td>{d.gifsRemaining ?? "—"}</Td>
              <Td>
                <StatusDot $online={isDeviceOnline(d.network_type)} />
                {networkLabel(d.network_type)}
              </Td>
            </Row>
            );
          })}
        </tbody>
      </Table>

      <Pager>
        <PagerButton
          type="button"
          disabled={!hasPrevPage || isFetching}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </PagerButton>
        <span className="admin-devices__page">Page {page}</span>
        <PagerButton
          type="button"
          disabled={!hasNextPage || isFetching}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </PagerButton>
      </Pager>
    </Wrapper>
  );
};

export default DeviceMonitor;
